import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { saveIntendedUrl } from '../../utils/persistentNavigation';
import { PAGE_SEO, SEO_DEFAULTS } from '../../utils/seoConfig';
import {
  generateOrganizationSchema,
  generateWebSiteSchema,
  generateHeroVideoSchema,
  generateFAQSchema,
  JsonLd,
} from '../../utils/structuredData';
import { trackEvent } from '../../utils/analytics';
import { BENCHMARK_LABEL, BENCHMARK_MONTH } from '../../content/benchmarkClaim';
import AIEClose from '../AIEmployees/sections/AIEClose';
import AIENoraFilm from '../AIEmployees/sections/AIENoraFilm';
import SAModalities from '../SuperAgents/sections/SAModalities';
import A2AStat from '../AgentToAgent/sections/A2AStat';
import HomeFold from './HomeFold';
import HomeVideoTile from './HomeVideoTile';
import HomeHiringStrip from './HomeHiringStrip';
import HomeLogoRow from './HomeLogoRow';
import HomeShowcase from './HomeShowcase';
import ConnectorStrip from '../Marketing/ConnectorStrip';
import HomeProduct from './HomeProduct';
import HomeFeatureCards from './HomeFeatureCards';
import HomeProse from './HomeProse';

import HomeTrustRow from './HomeTrustRow';
import HomeFaq, { HOME_FAQ_ITEMS } from './HomeFaq';
import MobileStickyCta from '../Marketing/MobileStickyCta';
import { HERO, MIDCTA } from './homeContent';
import '../AIEmployees/AIEmployeesLandingPage.css';
import './HomePage.css';

/**
 * HomePage — the logged-out homepage at "/": an AI-Employee-LED three-product
 * hub. Not a neutral directory: "AI employee" is the strategic keyword bet and
 * this is the highest-authority URL, so the H1 and canonical stay AI-Employee.
 *
 * PAGE ORDER (round-6 revamp, Aug 19 2026 — Nitish-ruled across the Aug 17-19
 * design sessions; decision record: Maeve's workspace work/home_round6/):
 *
 *   fold (HomeFold: H1 + locked sub-line, benchmark pill + 3 trust bullets,
 *         CTAs, and the INTERACTIVE DEMO FRAME — a coded, product-true
 *         workspace that replaced the autoplay hero video)
 *   → video tile        (the hire film, click-to-play, directly after the frame)
 *   → 01 AI Employees   (six promise cards + function chips + prose answer block)
 *   → mid-page CTA      (lean pass: no price — the cost answer lives in the FAQ)
 *   → 02 Super Agent    (artifact cards + benchmark credential + modality grid +
 *                        Try CTA + prose — the ask lands AFTER the full story)
 *   → 03 Agent-to-Agent (Connect CTA + ~30% stat)
 *   → 04 Your org       (EXAMPLE org chart, root = "You", shared component with
 *                        /ai-employees; the LIVE chart stays on /ai-organization)
 *   → FAQ               (7 items + FAQPage JSON-LD — adds "What is a shift?")
 *   → close (hire CTA)
 *
 *   Aug 19 (Nitish live): org proof strip REMOVED (section 04 carries the
 *   self-proof) and the section-01 role-story panel REMOVED — both per the
 *   decided round-6 mock. (HomeOrgStrip.js/HomeRolePanel.js were unmounted
 *   then and deleted 2026-08-20, CEL-1488.)
 *
 * KEY RULINGS BAKED IN (Aug 17-19 sessions; the Aug 6 rulings below the fold
 * are unchanged):
 *  - NO PRICE IN THE FOLD: a shift is undefined at first sight; the cost
 *    answer lives in the FAQ where "What is a shift?" gives it room.
 *  - The H1 is prod's exact markup (static aria-label + RoleRotator) — the
 *    revamp changes the fold AROUND the H1, never the H1.
 *  - The hero video did NOT leave the page: it's the click-to-play tile (so
 *    the video sitemap entry + VideoObject schema stay honest) and the film
 *    inside the "Get a demo" modal.
 *  - CTA verb grammar unchanged: HIRE = employee, TRY = super agent,
 *    CONNECT = A2A; close = the one brand-level CTA.
 *  - Credits line split by audience unchanged (200-credits line only under
 *    the super-agent Try CTA).
 *  - Every fold/tile/modal string traces to homeContent.js (the copy
 *    assertion enforces it).
 *
 * The product deep-dives live on /ai-employees, /super-agents, /agent-to-agent
 * (each self-canonical); this page passes crawlable link equity to all three.
 * Sitewide entity schema (Organization + WebSite) lives HERE.
 *
 * Design docs: agent_docs/homepage_three_product_hub_prd.md (Aug 6 hub) +
 * the round-6 port PRD in Maeve's workspace (work/home_round6/).
 */

/* Function chips: each word is a crawlable internal link into its matching
   role page — homepage link equity flowing into the role pages. DRAFT word
   list; final list is Rhea's call (SEO lane). */
const FUNCTIONS = [
  { label: 'Research', to: '/ai-employees/ai-research-assistant' },
  { label: 'Outreach', to: '/ai-employees/ai-sdr' },
  { label: 'Support', to: '/ai-employees/ai-customer-support-agent' },
  { label: 'Data & Analysis', to: '/ai-employees/ai-data-analyst' },
  { label: 'Marketing', to: '/ai-employees/ai-marketing-manager' },
  { label: 'Design', to: '/ai-employees/ai-designer' },
  { label: 'SEO', to: '/ai-employees/ai-seo-specialist' },
  { label: 'Engineering', to: '/ai-employees/ai-software-engineer' },
  { label: 'Operations', to: '/ai-employees/ai-operations-manager' },
];

/* Agent platforms in the A2A section (Aug 7, Nitish): the SAME seven entries
   as the /agent-to-agent integrations wall (A2AIntegrations.js), icons
   included — the homepage chip row is that wall in miniature. Crawlable
   internal links passing homepage equity to the spoke pages. */
/* iconLight (Aug 23, adaptive theme): glyphs drawn for the dark page vanish
   on the light face, so entries with a light-face twin carry it; CSS in
   MarketingAdaptiveTheme.css shows exactly one of the pair per face.
   Single-variant (full-color) marks render on both faces unchanged. */
const INTEGRATIONS = [
  { label: 'OpenClaw', to: '/openclaw', icon: '/assets/icons/agents/openclaw.svg' },
  { label: 'Linear', to: '/linear', icon: '/assets/icons/linear.svg', iconLight: '/assets/icons/linear-dark.svg' },
  { label: 'Claude Code', to: '/claude-code', icon: '/assets/icons/agents/claude.png' },
  { label: 'Cursor', to: '/cursor', icon: '/assets/icons/agents/cursor-dark.svg', iconLight: '/assets/icons/agents/cursor-light.svg' },
  { label: 'Codex', to: '/codex', icon: '/assets/icons/agents/openai-light.svg', iconLight: '/assets/icons/agents/openai-dark.svg' },
  { label: 'Hermes', to: '/hermes', icon: '/assets/icons/agents/hermes-light.png?v=7', iconLight: '/assets/icons/agents/hermes-dark.png?v=7' },
  { label: 'Your own agents', to: '/agent-to-agent', icon: null },
];

const HomePage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  /* section_view: the same per-section funnel /ai-employees has.
     SELECTOR-BASED deliberately: home's sections are shared components used
     by other pages, so tagging them with data-attributes would leak wrong
     page labels elsewhere. Observing existing stable classes/ids changes
     zero markup. Threshold logic mirrors AIEmployeesLandingPage.js (20% OR
     half a viewport, tall-section-safe). A2AStat keeps its own "stat" event.
     Round-6 delta: '.aie-hero' → '.home-fold' (label stays 'hero' so the
     funnel reads continue), + 'video_tile'. */
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const SECTIONS = [
      ['.home-fold', 'hero'],
      ['.home-lr', 'logo_row'],
      ['.home-hs', 'hiring_strip'],
      ['.home-vtile-wrap', 'video_tile'],
      ['#home-ai-employees', 'aie_product'],
      ['.conn-strip', 'connectors'],
      ['.home-midcta', 'midcta'],
      ['#home-super-agent', 'super_agent'],
      ['.home-show', 'sa_showcase'],
      ['.sa-mod', 'modalities'],
      ['#home-agent-to-agent', 'a2a_product'],

      ['.aie-nora', 'nora_film'],
      ['.home-tr', 'trust_row'],
      ['.aie-faq', 'faq'],
      ['.aie-close', 'close'],
    ];
    const seen = new Set();
    const labelByEl = new Map();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        const vh = (en.rootBounds && en.rootBounds.height) || window.innerHeight;
        const enough = en.intersectionRatio >= 0.2
          || en.intersectionRect.height >= vh * 0.5;
        if (!enough) return;
        const label = labelByEl.get(en.target);
        if (!label || seen.has(label)) return;
        seen.add(label);
        trackEvent('section_view', { section: label, page: 'home' });
        io.unobserve(en.target);
      });
    }, { threshold: [0.05, 0.1, 0.15, 0.2] });
    SECTIONS.forEach(([sel, label]) => {
      const el = document.querySelector(sel);
      if (el) { labelByEl.set(el, label); io.observe(el); }
    });
    return () => io.disconnect();
  }, []);

  const hireLabel = HERO.cta;
  const handleHire = (location) => () => {
    trackEvent('cta_click', { cta_name: 'hire_primary', compare_page: 'home', location });
    if (!currentUser) {
      saveIntendedUrl('/ai_employee');
      navigate('/signup');
      return;
    }
    navigate('/ai_employee');
  };

  /* Section LINKS report themselves too (Aug 22). Four crawlable links —
     Explore AI Employees, See what one prompt made, See it in action, and the
     benchmark credit line — fired no event, so they were invisible in every
     per-CTA ranking: not zero clicks, uncounted clicks. Same event shape as
     every other CTA, so they rank alongside the buttons and we can finally
     answer "did home send them to a product page, and did they sign up". */
  const trackSectionLink = (ctaName, location) => () => {
    trackEvent('cta_click', { cta_name: ctaName, compare_page: 'home', location });
  };

  /* TRY = the super agent's verb (one prompt, one artifact, no commitment). */
  const handleTrySuperAgent = () => {
    trackEvent('cta_click', { cta_name: 'try_super_agent', compare_page: 'home', location: 'sa_after_proof' });
    // This CTA sells the CHAT product, so it lands on New Chat, which is "/" again since 2026-09-16
    // (Nitish; the 09-12 hire-page front door was reverted). The hire CTAs land on /ai_employee.
    if (!currentUser) {
      saveIntendedUrl('/');
      navigate('/signup');
      return;
    }
    navigate('/');
  };

  /* Close section = the one BRAND-level CTA. Same destination as hire (the
     flagship intent survives signup), its own analytics name so the funnel
     can compare brand vs product verbs. */
  const handleTryCellCog = () => {
    trackEvent('cta_click', { cta_name: 'try_cellcog_free', compare_page: 'home', location: 'close' });
    if (!currentUser) {
      saveIntendedUrl('/ai_employee');
      navigate('/signup');
      return;
    }
    navigate('/ai_employee');
  };

  /* A2A "Connect your agent" is a quiet LINK now (Aug 23): a genuinely
     different action for a different buyer (the API-key path), so it stops
     dressing like a CTA. Same cta_name (connect_agent) — event names are
     identifiers, not copy. Its old co-located "See it in action" link pointed
     at the SAME page, so the pair collapsed into the one link (the
     see_it_in_action series is retired with it). */

  const seo = PAGE_SEO.home;
  // "/" is self-canonical: the three-product hub is a distinct page from the
  // /ai-employees deep-dive (which is now self-canonical too).
  const canonicalUrl = SEO_DEFAULTS.url;
  const ogImage = `${SEO_DEFAULTS.url}/og-image.png`;

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="twitter:image" content={ogImage} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&display=swap"
          rel="stylesheet"
        />
        {/* The Aug-9 hero-poster preload is GONE with the autoplay hero: the
            fold's LCP element is now prerendered demo-frame markup (text +
            hairlines — no fetch), and the tile's thumbnail is lazy below the
            fold. A high-priority preload for a lazy image would be a straight
            LCP regression. */}
      </Helmet>
      {/* Sitewide brand-entity schema lives on the homepage. */}
      <JsonLd data={generateOrganizationSchema()} />
      <JsonLd data={generateWebSiteSchema()} />
      {/* The hire film stays on the page (tile + modal), so its VideoObject
          schema and video-sitemap entry stay honest — position never mattered
          to either. The /super-agents proof-wall clips stay unmarked. */}
      <JsonLd data={generateHeroVideoSchema()} />
      {/* FAQPage schema mirrors the visible FAQ band below, verbatim. */}
      <JsonLd data={generateFAQSchema(HOME_FAQ_ITEMS)} />

      <div className="aie-landing home-page dark">
        {/* The fold: H1 (prod's, untouched) + locked sub-line + benchmark
            pill + trust bullets + CTAs + the interactive demo frame. */}
        <HomeFold onPrimary={handleHire('hero')} />

        {/* "Used by people who work at" (was "People use CellCog at") (Nitish, Sep 25 2026): right under the demo.
            The hiring strip moved under "Hire for any role." so two moving
            strips never stack. */}
        <HomeLogoRow />

        {/* The hire film, one scroll beat after the demo frame. */}
        <HomeVideoTile />


        <div id="home-products" />

        {/* 01 — AI Employees (the flagship: first and largest). */}
        <HomeProduct
          id="home-ai-employees"
          eyebrow="01 · AI Employees"
          title="Hire for any role."
          sub="A general-purpose AI worker you hire for a role. They show up with a name, an inbox, and a schedule, and run the work inside the goals and permissions you set."
          preCta={
            <>
            {/* Real recent hires, directly under "Hire for any role." (moved here
                from under the demo, Nitish Sep 25 2026). */}
            <HomeHiringStrip />
            {/* Six promise cards replaced the check-bullet list (Aug 28 2026,
                Nitish's live pick: Option A quiet cards — copy canon in
                homeContent.AIE_CARDS, decision record work/home_sixbox/). */}
            <HomeFeatureCards />
            <nav className="home-funcs" aria-label="Roles you can hire an AI employee for">
              {FUNCTIONS.map((f) => (
                <Link
                  key={f.label}
                  className="home-func"
                  to={f.to}
                  onClick={() => trackEvent('cta_click', { cta_name: 'function_chip', compare_page: 'home', location: 'aie_section', chip: f.label })}
                >
                  {f.label}
                </Link>
              ))}
            </nav>
            </>
          }
          primary={{ label: hireLabel, onClick: handleHire('aie_section') }}
          link={{
            label: 'Explore AI Employees',
            to: '/ai-employees',
            onClick: trackSectionLink('explore_ai_employees', 'aie_section'),
          }}
        >

          {/* Crawlable prose: the definitional answer, on the highest-authority
              URL. Final copy through Rhea (SEO lane). Collapses behind "Read
              more" on phones (full text always in the DOM). */}
          <HomeProse>
            <b>What is an AI employee?</b> An AI employee is a general-purpose AI
            worker you hire for a role: marketing, sales outreach, support,
            research, data analysis, operations. They have a name, their own email
            inbox, a schedule of shifts, persistent memory, and a task board.
            You set the goals and the permissions; they plan their own work,
            report back every shift, and remember everything they learned. When
            the job needs more hands, they pull in your other AI employees, and
            pull you in when a decision needs your judgment.
          </HomeProse>
        </HomeProduct>

        {/* The connector wall: "{TOOL_COUNT} tools" is a claim, recognisable marks
            are proof. It sits right after the AI-employee story, where the
            "will it work with my stack?" question actually forms. */}
        <ConnectorStrip />

        {/* Mid-page CTA — lean pass (Aug 17): no price here; the cost answer
            lives in the FAQ, where "What is a shift?" gives it room. */}
        <section className="home-midcta">
          <h3 className="aie-display">{MIDCTA.title}</h3>
          <p>{MIDCTA.sub}</p>
          <button
            type="button"
            data-msc-suppress
            className="aie-btn aie-btn-primary"
            onClick={handleHire('midcta')}
          >
            {hireLabel}
            <ArrowRightOutlined />
          </button>
        </section>

        <div className="aie-divider" />

        {/* 02 — Super Agent (the base everything runs on). TRY is its verb. */}
        <HomeProduct
          id="home-super-agent"
          eyebrow="02 · Super Agent"
          title="One prompt."
          titleAccent="The whole job."
          sub="The deep-reasoning base every AI employee runs on. One prompt in, a finished artifact out."
        />
        {/* The single-prompt proof, as click-to-play artifact cards (Aug 21):
            home no longer autoplays anything. The "single prompt" line moved
            inside the component's own sub-head. */}
        <HomeShowcase />
        {/* Dated benchmark credential (the standalone band was killed; the
            badge lives in the hero, the receipt lives here, under the work). */}
        <p className="home-credline">
          Ranked <Link to="/benchmarks" onClick={trackSectionLink('bench_credline', 'sa_showcase')}>#1 on DeepResearch Bench</Link> ({BENCHMARK_MONTH}), the
          deep reasoning behind every employee.
        </p>
        <SAModalities />
        {/* 02's ask, after the full capability story (round-6 mock order,
            Nitish Aug 19): showcase → receipt → modality grid → the ask. */}
        <section className="home-sa-cta">
          <div className="home-product__ctas">
            <button
              type="button"
              data-msc-suppress
              className="aie-btn aie-btn-primary"
              onClick={handleTrySuperAgent}
            >
              {hireLabel}
              <ArrowRightOutlined />
            </button>
            <Link
              className="home-product__link"
              to="/super-agents"
              onClick={trackSectionLink('see_what_one_prompt_made', 'sa_after_proof')}
            >
              See what one prompt made <ArrowRightOutlined />
            </Link>
          </div>
          <p className="home-product__support">{HERO.support}</p>
        </section>
        <section className="home-prose-band">
          <HomeProse>
            <b>What is an AI super agent?</b> A super agent takes one prompt and
            runs the whole job. It <b>plans</b> the work, breaking your ask into
            concrete steps. It <b>researches</b> across the live web with cited
            sources. It <b>executes</b>: writing code, building dashboards,
            generating video, audio, images, and documents. And it <b>delivers</b> a
            finished artifact, not a draft of one. CellCog&rsquo;s super agent is the
            deep-reasoning engine behind every AI employee on the platform,
            ranked {BENCHMARK_LABEL}.
          </HomeProse>
        </section>

        <div className="aie-divider" />

        {/* 03 — Agent-to-Agent (the platform, open to other agents). */}
        <HomeProduct
          id="home-agent-to-agent"
          eyebrow="03 · Agent-to-Agent"
          title="The whole platform,"
          titleAccent="open to your agents."
          sub="Every software will have two interfaces: one for humans, one for agents. CellCog ships both. The entire platform is available to your agents."
          preCta={
            <nav className="home-funcs home-works" aria-label="Agent platforms CellCog works with">
              <span className="home-works__label">Works with</span>
              {INTEGRATIONS.map((it) => (
                <Link
                  key={it.label}
                  className="home-func home-works__chip"
                  to={it.to}
                  onClick={() => trackEvent('cta_click', { cta_name: 'integration_chip', compare_page: 'home', location: 'a2a_section', chip: it.label })}
                >
                  {it.icon
                    ? (
                      <>
                        <img className={it.iconLight ? 'mkt-face-dark' : undefined} src={it.icon} alt="" loading="lazy" />
                        {it.iconLight && <img className="mkt-face-light" src={it.iconLight} alt="" loading="lazy" />}
                      </>
                    )
                    : <span className="home-works__glyph" aria-hidden="true">{'</>'}</span>}
                  {it.label}
                </Link>
              ))}
            </nav>
          }
          link={{
            label: 'Connect your agent',
            to: '/agent-to-agent',
            onClick: trackSectionLink('connect_agent', 'a2a_section'),
          }}
        />
        <A2AStat />

        <div className="aie-divider" />

        {/* 04 — The proof (org-chart section REMOVED Aug 31, Nitish: the
            example org read as heavy setup work to a first-time visitor —
            our hirers aren't building reporting structures yet; the deeper
            org story stays off home). The Nora film is now home's proof
            beat (joined Aug 24). Grok-style lazy player: the
            7.7MB video mounts only on viewport intersection, poster is
            lazy-loaded, frame reserves its box — zero LCP/CLS impact (the
            fold stays prerendered HTML). page="home" keeps its analytics
            separate from the /ai-employees placement. */}
        <AIENoraFilm page="home" />

        {/* Trust row (Nitish, Sep 16 2026): two tiles, CASA Tier 2 + Verified
            for Gmail, in the gap he pointed at between the film and the FAQ.
            Claims per org canon `security-claims`. */}
        <HomeTrustRow />

        {/* FAQ — visible band + the FAQPage schema above. */}
        <HomeFaq />

        {/* Close = the ONE brand-level CTA (CTA research, Aug 6 ruling): by
            here the visitor has seen all three products, so the door is the
            brand. Everything above keeps its product verb (Hire/Try/Connect). */}
        <AIEClose
          ctaLabel={hireLabel}
          onPrimary={handleTryCellCog}
          title="Hire your first AI employee today."
          sub="Try it free. No credit card needed."
          demoPage="/"
        />

        {/* Mobile-only sticky bottom CTA (the standing all-pages pattern).
            ONE constant label — the flagship action, pulled from the content
            layer (never a second name for one action). It auto-hides whenever
            ANY inline CTA (data-msc-suppress) is on screen, and slides away
            while a modal is open (body.cc-modal-open — one ask at a time). */}
        <MobileStickyCta label={hireLabel} onClick={handleHire('sticky_mobile')} />
      </div>
    </>
  );
};

export default HomePage;
